<template>
    <div>
        <h4 class="text-slate-800 text-base font-medium mb-8">
            {{
                redemptionType?.name === 'Other'
                    ? 'Redemption'
                    : redemptionType?.name
            }}
        </h4>

        <!-- HOTEL -->
        <div v-if="redemptionType?.slug === 'hotel'">
            <div class="mb-8">
                <Input
                    label="Hotel name"
                    placeholder="Hotel name"
                    id="hotel-name"
                >
                    <template #input>
                        <HotelDropdown
                            :externalActions="true"
                            @propertyChanged="hotelSelected"
                            :hotel="activeRedemption.name"
                        />
                    </template>
                </Input>
            </div>

            <div class="grid grid-cols-2 gap-4 mb-8">
                <div class="col-span-1">
                    <Input
                        label="Check in"
                        placeholder="Check in"
                        name="start_date"
                        id="check-in"
                        :options="{ icon: 'calendar', iconPosition: 'left' }"
                        :errors="tripFormErrors"
                    >
                        <template #input>
                            <Flatpickr
                                :config="dateStartConfig"
                                v-model="activeRedemption.start_date"
                                name="start_date"
                                id="check-in"
                                class="relative block w-full h-10 rounded border border-slate-200 bg-white py-1.5 pl-10 text-gray-900placeholder:text-slate-500 focus:z-10 focus:ring-0 focus:border-slate-800 hover:border-slate-800 placeholder:text-sm"
                                placeholder="Check in"
                                @on-change="onStartChange"
                                :class="{
                                    '!border-red-500':
                                        tripFormErrors['start_date'],
                                }"
                            />
                        </template>
                    </Input>
                </div>
                <div class="col-span-1">
                    <Input
                        label="Check out"
                        placeholder="Check out"
                        name="end_date"
                        id="check-out"
                        :options="{ icon: 'calendar', iconPosition: 'left' }"
                    >
                        <template #input>
                            <Flatpickr
                                :config="dateEndConfig"
                                v-model="activeRedemption.end_date"
                                name="end_date"
                                id="check-out"
                                class="relative block w-full h-10 rounded border border-slate-200 bg-white py-1.5 pl-10 text-gray-900placeholder:text-slate-500 focus:z-10 focus:ring-0 focus:border-slate-800 hover:border-slate-800 placeholder:text-sm"
                                placeholder="Check out"
                                @on-change="onEndChange"
                            />
                        </template>
                    </Input>
                </div>
            </div>
        </div>
        <!-- // END HOTEL -->

        <!-- FLIGHT -->
        <div v-if="redemptionType?.slug === 'flight'">
            <div class="grid grid-cols-2 gap-4 mb-2">
                <div class="col-span-1">
                    <Input
                        label="Flight date"
                        placeholder="Flight date"
                        name="start_date"
                        id="depart-date"
                        :options="{ icon: 'calendar', iconPosition: 'left' }"
                        :errors="tripFormErrors"
                    >
                        <template #input>
                            <Flatpickr
                                :config="dateStartConfig"
                                v-model="activeRedemption.start_date"
                                name="flight_start_date"
                                id="depart-date"
                                class="relative block w-full h-10 rounded border border-slate-200 bg-white py-1.5 pl-10 text-gray-900placeholder:text-slate-500 focus:z-10 focus:ring-0 focus:border-slate-800 hover:border-slate-800 placeholder:text-sm"
                                placeholder="Departure date"
                                @on-change="onStartChange"
                                :class="{
                                    '!border-red-500':
                                        tripFormErrors['start_date'],
                                }"
                            />
                        </template>
                    </Input>
                </div>
                <div class="col-span-1">
                    <Input
                        label="Arrival date"
                        placeholder="Arrival date"
                        id="arrival-date"
                        :options="{ icon: 'calendar', iconPosition: 'left' }"
                        v-if="differentArrivalDate"
                    >
                        <template #input>
                            <Flatpickr
                                :config="dateEndConfig"
                                v-model="activeRedemption.end_date"
                                name="flight_end_date"
                                id="arrival-date"
                                class="relative block w-full h-10 rounded border border-slate-200 bg-white py-1.5 pl-10 text-gray-900placeholder:text-slate-500 focus:z-10 focus:ring-0 focus:border-slate-800 hover:border-slate-800 placeholder:text-sm"
                                placeholder="Arrival date"
                                @on-change="onEndChange"
                            />
                        </template>
                    </Input>
                </div>
            </div>
            <label class="text-sm text-slate-800 flex items-center gap-2 mb-8">
                <input
                    class="form-checkbox"
                    type="checkbox"
                    value="1"
                    v-model="differentArrivalDate"
                />
                <span>Arrival date is different</span>
            </label>

            <TravelSegments
                travel-number-label="Flight number"
                @segmentsUpdated="handleSegmentsUpdated"
                :segments="segments"
            />

            <div class="grid grid-cols-2 gap-4 mb-8">
                <div class="col-span-1">
                    <Input
                        id="number-of-travelers"
                        name="number_of_travelers"
                        label="Number of travelers"
                    >
                        <template #input>
                            <input
                                type="number"
                                step="1"
                                min="0"
                                pattern="[0-9]*"
                                placeholder="Number of travelers"
                                class="w-full h-10 text-sm rounded border border-slate-200"
                                v-model="activeRedemption.number_of_travelers"
                            />
                        </template>
                    </Input>
                </div>
                <div class="col-span-1">
                    <div>
                        <label
                            class="block text-slate-600 text-sm font-medium leading-tight mb-2"
                        >
                            Class of service
                        </label>
                        <UiSingleSelect
                            :options="classOptions"
                            v-model="classOfService"
                            labelKey="label"
                            valueKey="value"
                            :label="
                                classOfService?.label
                                    ? classOfService.label
                                    : 'Class of service'
                            "
                            buttonClass="w-full h-10 text-sm rounded border border-slate-200 font-medium whitespace-nowrap rounded inline-flex justify-between items-center gap-x-1.5 bg-white px-3 py-2 text-slate-500 font-normal text-sm focus:outline-none disabled:opacity-50 disabled:text-slate-800 disabled:bg-white disabled:border-slate-300"
                        />
                    </div>
                </div>
            </div>
        </div>
        <!-- // END FLIGHT -->

        <!-- TRAIN  -->
        <div v-if="redemptionType?.slug === 'train'">
            <div class="grid grid-cols-2 gap-4 mb-2">
                <div class="col-span-1">
                    <Input
                        label="Travel date"
                        placeholder="Travel date"
                        name="start_date"
                        id="travel-date"
                        :options="{ icon: 'calendar', iconPosition: 'left' }"
                        :errors="tripFormErrors"
                    >
                        <template #input>
                            <Flatpickr
                                :config="dateStartConfig"
                                v-model="activeRedemption.start_date"
                                name="travel_start_date"
                                id="travel-date"
                                class="relative block w-full h-10 rounded border border-slate-200 bg-white py-1.5 pl-10 text-gray-900placeholder:text-slate-500 focus:z-10 focus:ring-0 focus:border-slate-800 hover:border-slate-800 placeholder:text-sm"
                                placeholder="Travel date"
                                @on-change="onStartChange"
                                :class="{
                                    '!border-red-500':
                                        tripFormErrors['start_date'],
                                }"
                            />
                        </template>
                    </Input>
                </div>
                <div class="col-span-1">
                    <Input
                        label="Arrival date"
                        placeholder="Arrival date"
                        id="arrival-date"
                        :options="{ icon: 'calendar', iconPosition: 'left' }"
                        v-if="differentArrivalDate"
                    >
                        <template #input>
                            <Flatpickr
                                :config="dateEndConfig"
                                v-model="activeRedemption.end_date"
                                name="travel_end_date"
                                id="arrival-date"
                                class="relative block w-full h-10 rounded border border-slate-200 bg-white py-1.5 pl-10 text-gray-900placeholder:text-slate-500 focus:z-10 focus:ring-0 focus:border-slate-800 hover:border-slate-800 placeholder:text-sm"
                                placeholder="Arrival date"
                                @on-change="onEndChange"
                            />
                        </template>
                    </Input>
                </div>
            </div>
            <label class="text-sm text-slate-800 flex items-center gap-2 mb-8">
                <input
                    class="form-checkbox"
                    type="checkbox"
                    value="1"
                    v-model="differentArrivalDate"
                />
                <span>Arrival date is different</span>
            </label>

            <TravelSegments
                travel-number-label="Train number"
                @segmentsUpdated="handleSegmentsUpdated"
                :segments="segments"
            />

            <div class="grid grid-cols-2 gap-4 mb-8">
                <div class="col-span-1">
                    <Input
                        id="number-of-travelers"
                        name="number_of_travelers"
                        label="Number of travelers"
                    >
                        <template #input>
                            <input
                                type="number"
                                step="1"
                                pattern="[0-9]*"
                                placeholder="Number of travelers"
                                class="w-full h-10 text-sm rounded border border-slate-200"
                                v-model="activeRedemption.number_of_travelers"
                            />
                        </template>
                    </Input>
                </div>
                <div class="col-span-1">
                    <div>
                        <label
                            class="block text-slate-600 text-sm font-medium leading-tight mb-2"
                        >
                            Class of service
                        </label>
                        <UiSingleSelect
                            :options="classOptions"
                            v-model="classOfService"
                            labelKey="label"
                            valueKey="value"
                            :label="
                                classOfService?.label
                                    ? classOfService.label
                                    : 'Class of service'
                            "
                            buttonClass="w-full h-10 text-sm rounded border border-slate-200 font-medium whitespace-nowrap rounded inline-flex justify-between items-center gap-x-1.5 bg-white px-3 py-2 text-slate-500 font-normal text-sm focus:outline-none disabled:opacity-50 disabled:text-slate-800 disabled:bg-white disabled:border-slate-300"
                        />
                    </div>
                </div>
            </div>
        </div>
        <!-- // END TRAIN -->

        <!-- RENTAL CAR-->
        <div v-if="redemptionType?.slug === 'rental-car'">
            <div class="grid grid-cols-2 gap-4 mb-8">
                <div class="col-span-1">
                    <Input
                        label="Pick-up date"
                        placeholder="Pick-up"
                        name="start_date"
                        id="rent-start"
                        :options="{ icon: 'calendar', iconPosition: 'left' }"
                        :class="{
                            '!border-red-500': tripFormErrors['start_date'],
                        }"
                        :errors="tripFormErrors"
                    >
                        <template #input>
                            <Flatpickr
                                :config="dateStartConfig"
                                v-model="activeRedemption.start_date"
                                name="rent_start_date"
                                id="rent-start"
                                class="relative block w-full h-10 rounded border border-slate-200 bg-white py-1.5 pl-10 text-gray-900placeholder:text-slate-500 focus:z-10 focus:ring-0 focus:border-slate-800 hover:border-slate-800 placeholder:text-sm"
                                placeholder="Start"
                                @on-change="onStartChange"
                            />
                        </template>
                    </Input>
                </div>
                <div class="col-span-1">
                    <Input
                        label="Drop-off date"
                        placeholder="Drop-off"
                        id="rent-end"
                        :options="{ icon: 'calendar', iconPosition: 'left' }"
                    >
                        <template #input>
                            <Flatpickr
                                :config="dateEndConfig"
                                placeholder="End"
                                v-model="activeRedemption.end_date"
                                name="rent_end_date"
                                id="rent-end"
                                class="relative block w-full h-10 rounded border border-slate-200 bg-white py-1.5 pl-10 text-gray-900placeholder:text-slate-500 focus:z-10 focus:ring-0 focus:border-slate-800 hover:border-slate-800 placeholder:text-sm"
                                @on-change="onEndChange"
                            />
                        </template>
                    </Input>
                </div>
            </div>

            <div class="grid grid-cols-2 gap-4 mb-8">
                <div class="col-span-1">
                    <Input
                        label="Pick-up location"
                        id="pick-up-location"
                        placeholder="Pick-up"
                        name="pick_up_location"
                        v-model:inputValue="activeRedemption.pickup_location"
                        :options="{ inputClass: 'h-10' }"
                    />
                </div>
                <div class="col-span-1">
                    <Input
                        label="Drop-off location"
                        id="drop-off-location"
                        placeholder="Drop-off"
                        name="drop_off_location"
                        v-model:inputValue="activeRedemption.dropoff_location"
                        :options="{ inputClass: 'h-10' }"
                    />
                </div>
            </div>
        </div>
        <!-- // END RENTAL CAR -->

        <!-- OTHER -->
        <div v-if="redemptionType?.slug === 'other'">
            <div class="mb-8">
                <Input
                    label="Redemption name"
                    placeholder="Name"
                    id="redemption-name"
                    v-model:inputValue="activeRedemption.name"
                    :options="{ inputClass: 'h-10' }"
                />
            </div>

            <div class="grid grid-cols-2 gap-4 mb-8">
                <div class="col-span-1">
                    <Input
                        label="Redemption date"
                        placeholder="Redemption date"
                        id="redemption-date"
                        :options="{
                            icon: 'calendar',
                            iconPosition: 'left',
                            inputClass: 'h-10',
                        }"
                    >
                        <template #input>
                            <Flatpickr
                                :config="dateStartConfig"
                                v-model="activeRedemption.start_date"
                                name="redemption_date"
                                id="redemption-date"
                                class="relative block w-full h-10 rounded border border-slate-200 bg-white py-1.5 pl-10 text-gray-900placeholder:text-slate-500 focus:z-10 focus:ring-0 focus:border-slate-800 hover:border-slate-800 placeholder:text-sm"
                                placeholder="Redemption date"
                            />
                        </template>
                    </Input>
                </div>
            </div>

            <div class="mb-8">
                <Input
                    label="Location"
                    placeholder="Location"
                    id="other-location"
                    v-model:inputValue="activeRedemption.other_location"
                    :options="{ inputClass: 'h-10' }"
                />
            </div>

            <div class="grid grid-cols-2 gap-4 mb-8">
                <div class="col-span-1">
                    <Input
                        id="number-of-travelers"
                        name="number_of_travelers"
                        label="Number of travelers"
                    >
                        <template #input>
                            <input
                                type="number"
                                step="1"
                                pattern="[0-9]*"
                                placeholder="Number of travelers"
                                class="w-full h-10 text-sm rounded border border-slate-200"
                                v-model="activeRedemption.number_of_travelers"
                            />
                        </template>
                    </Input>
                </div>

                <div class="col-span-1">
                    <div>
                        <label
                            class="block text-slate-600 text-sm font-medium leading-tight mb-2"
                        >
                            Class of service
                        </label>
                        <UiSingleSelect
                            :options="classOptions"
                            v-model="classOfService"
                            labelKey="label"
                            valueKey="value"
                            :label="
                                classOfService?.label
                                    ? classOfService.label
                                    : 'Class of service'
                            "
                            buttonClass="w-full h-10 text-sm rounded border border-slate-200 font-medium whitespace-nowrap rounded inline-flex justify-between items-center gap-x-1.5 bg-white px-3 py-2 text-slate-500 font-normal text-sm focus:outline-none disabled:opacity-50 disabled:text-slate-800 disabled:bg-white disabled:border-slate-300"
                        />
                    </div>
                </div>
            </div>
        </div>
        <!-- // END OTHER -->

        <div class="mb-8">
            <Textarea
                label="Notes"
                placeholder="Any notes and details on this redemption"
                id="notes"
                v-model:inputValue="activeRedemption.notes"
            />
        </div>
    </div>
</template>
<script setup>
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useTripsStore } from '@/store/trips';
import Flatpickr from 'vue-flatpickr-component';
import TravelSegments from '@/components/trips/travel-segments.vue';
import Textarea from '@/components/ui/inputs/ui-textarea.vue';
import Input from '@/components/ui/inputs/ui-input.vue';
import Select from '@/components/ui/inputs/ui-select.vue';
import UiSingleSelect from '@/components/ui/inputs/single-select/ui-single-select.vue';
import Filters from '@/filters';

import HotelDropdown from '@/components/trips/modals/steps/hotel-dropdown.vue';

const tripsStore = useTripsStore();
const {
    activeRedemption,
    classOptions,
    redemptionTypes,
    showRedemptionCreateModal,
    tripFormErrors,
} = storeToRefs(tripsStore);

const classOfService = computed({
    get: () => {
        return typeof activeRedemption.value.class_of_service === 'string'
            ? {
                  value: activeRedemption.value.class_of_service,
                  label: Filters.ucWords(
                      activeRedemption.value.class_of_service
                  ),
              }
            : activeRedemption.value.class_of_service;
    },
    set: (value) => {
        activeRedemption.value.class_of_service = value?.value || null;
    },
});

const redemptionType = computed(() => {
    return redemptionTypes.value.find(
        (redemptionType) =>
            redemptionType.id === activeRedemption.value.redemption_type_id
    );
});

const segments = computed(() => {
    return activeRedemption.value.travel_segments &&
        activeRedemption.value.travel_segments.length
        ? activeRedemption.value.travel_segments
        : [{ origin: null, destination: null, travel_number: null }];
});

watch(showRedemptionCreateModal, (val) => {
    if (val) {
        differentArrivalDate.value = activeRedemption.value.end_date
            ? true
            : false;
    }
});

const dateStartConfig = ref({ dateFormat: 'm/d/Y' });

const dateEndConfig = ref({ dateFormat: 'm/d/Y' });

const onStartChange = (selectedDates, dateStr, instance) => {
    dateEndConfig.value.minDate = dateStr;
};

const onEndChange = (selectedDates, dateStr, instance) => {
    dateStartConfig.value.maxDate = dateStr;
};

const handleSegmentsUpdated = (segments) => {
    tripsStore.setSegments(segments);
};

const hotelSelected = (hotel) => {
    if (hotel) {
        activeRedemption.value.hotel_id = hotel.id;
        activeRedemption.value.name = hotel.name;
    } else {
        activeRedemption.value.hotel_id = null;
        activeRedemption.value.name = null;
    }
};

const differentArrivalDate = ref(false);

watch(differentArrivalDate, (val) => {
    if (!val) {
        activeRedemption.value.end_date = null;
    }
});
</script>
